import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { VehicleCard, VehicleCardSkeleton } from '@/components/VehicleCard';
import { FilterSidebar } from '@/components/FilterSidebar';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const INITIAL_PAGE_SIZE = 6;

export default function InventarioPage() {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: INITIAL_PAGE_SIZE,
    total: 0,
    total_pages: 1
  });

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await api.getCategories();
        setCategories(data);
        
        if (categorySlug) {
          const cat = data.find(c => c.slug === categorySlug);
          setCurrentCategory(cat || null);
        } else {
          setCurrentCategory(null);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [categorySlug]);

  // Parse filters from URL
  const getFiltersFromURL = useCallback(() => {
    const filters = {};
    for (const [key, value] of searchParams.entries()) {
      if (key === 'page') {
        filters.page = parseInt(value);
      } else if (['precio_min', 'precio_max', 'modelo_min', 'modelo_max'].includes(key)) {
        filters[key] = parseFloat(value);
      } else if (key === 'destacado') {
        filters[key] = value === 'true';
      } else {
        filters[key] = value;
      }
    }
    return filters;
  }, [searchParams]);

  const [filters, setFilters] = useState(getFiltersFromURL);

  // Fetch filter options
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const data = await api.getFilters();
        setFilterOptions(data);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, []);

  // Fetch vehicles (initial load or filter change)
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setVehicles([]);
    
    try {
      const params = {
        page: 1,
        page_size: INITIAL_PAGE_SIZE,
        ...filters
      };
      
      // Add category filter if viewing a specific category
      if (currentCategory) {
        params.categoria_id = currentCategory.id;
      }
      
      const data = await api.getVehicles(params);
      
      setVehicles(data.vehicles);
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error al cargar los vehículos');
    } finally {
      setLoading(false);
    }
  }, [filters, currentCategory]);

  // Load more vehicles
  const loadMoreVehicles = async () => {
    if (loadingMore || vehicles.length >= pagination.total) return;
    
    setLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      const params = {
        page: nextPage,
        page_size: INITIAL_PAGE_SIZE,
        ...filters
      };
      
      if (currentCategory) {
        params.categoria_id = currentCategory.id;
      }
      
      const data = await api.getVehicles(params);
      
      setVehicles(prev => [...prev, ...data.vehicles]);
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      console.error('Error loading more vehicles:', error);
      toast.error('Error al cargar más vehículos');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Update URL when filters change
  const handleFilterChange = (newFilters) => {
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'page') {
        params.set(key, value.toString());
      }
    });
    setSearchParams(params);
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setFilters({});
  };

  const pageTitle = currentCategory ? currentCategory.nombre : 'Inventario Completo';
  const pageSubtitle = currentCategory 
    ? `Explora nuestra selección de ${currentCategory.nombre.toLowerCase()}`
    : 'Explora todos nuestros vehículos disponibles';

  const hasMoreVehicles = vehicles.length < pagination.total;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative py-12 md:py-16 bg-card">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary text-sm font-bold uppercase tracking-widest mb-4">
              {currentCategory ? 'Categoría' : 'Nuestro Inventario'}
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight uppercase text-foreground mb-4">
              {pageTitle}
            </h1>
            <p className="text-muted-foreground">
              {pageSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Vehicles Section */}
      <section className="py-12 md:py-16" data-testid="vehicles-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-muted-foreground">
                Mostrando <span className="text-primary font-bold">{vehicles.length}</span> de{' '}
                <span className="text-primary font-bold">{pagination.total}</span> vehículos
              </p>
            </div>
          </div>

          {/* Content Layout */}
          <div className="flex gap-8">
            {/* Filters */}
            <FilterSidebar
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              vehicleCount={pagination.total}
            />

            {/* Vehicle Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[...Array(INITIAL_PAGE_SIZE)].map((_, i) => (
                    <VehicleCardSkeleton key={i} />
                  ))}
                </div>
              ) : vehicles.length > 0 ? (
                <>
                  <div id="vehicles-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vehicles-grid">
                    {vehicles.map((vehicle, index) => (
                      <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
                    ))}
                  </div>

                  {/* Load More Button */}
                  {hasMoreVehicles && (
                    <div className="flex justify-center mt-12">
                      <Button
                        onClick={loadMoreVehicles}
                        disabled={loadingMore}
                        className="btn-primary min-w-[200px]"
                        data-testid="load-more-btn"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Cargando...
                          </>
                        ) : (
                          <>
                            Ver Más Vehículos
                            <span className="ml-2 text-sm opacity-80">
                              ({pagination.total - vehicles.length} restantes)
                            </span>
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16" data-testid="no-results">
                  <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    No se encontraron vehículos
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Intenta ajustar los filtros de búsqueda
                  </p>
                  <Button onClick={handleClearFilters} className="bg-primary hover:bg-primary/90 text-white font-bold px-6 py-2">
                    Limpiar Filtros
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
